/**
 * Standardized Playback Platform event model (Sprint 6A).
 *
 * Providers normalize native events into this set. The Playback Host and
 * plugins subscribe via the Playback Event Bus — never to provider-specific APIs.
 *
 * Legacy PascalCase PlayerEvent names remain supported for backward compatibility;
 * see mapPlayerEventToPlatform / mapPlatformEventToPlayer.
 */
import { PlayerEvent } from "./PlayerEvents";

export var PlaybackPlatformEvent = Object.freeze({
  READY: "READY",
  PLAY: "PLAY",
  PAUSE: "PAUSE",
  STOP: "STOP",
  SEEK_START: "SEEK_START",
  SEEK_COMPLETE: "SEEK_COMPLETE",
  PROGRESS: "PROGRESS",
  BUFFERING: "BUFFERING",
  BUFFERED: "BUFFERED",
  ENDED: "ENDED",
  ERROR: "ERROR",
  QUALITY_CHANGED: "QUALITY_CHANGED",
  VOLUME_CHANGED: "VOLUME_CHANGED",
  MUTE_CHANGED: "MUTE_CHANGED",
  PLAYBACK_RATE_CHANGED: "PLAYBACK_RATE_CHANGED",
  FULLSCREEN_ENTER: "FULLSCREEN_ENTER",
  FULLSCREEN_EXIT: "FULLSCREEN_EXIT",
  SUBTITLE_CHANGED: "SUBTITLE_CHANGED",
  AUDIO_TRACK_CHANGED: "AUDIO_TRACK_CHANGED",
  // Platform extensions (still provider-agnostic)
  STATE_CHANGED: "STATE_CHANGED",
  SOURCE_CHANGED: "SOURCE_CHANGED",
  PROVIDER_CHANGED: "PROVIDER_CHANGED",
  LIFECYCLE_CHANGED: "LIFECYCLE_CHANGED",
  CAPABILITIES_CHANGED: "CAPABILITIES_CHANGED",
  DESTROYED: "DESTROYED",
  // Sprint 6B — UMXP session / pipeline / entitlements / intro / ads / live
  SESSION_CREATED: "SESSION_CREATED",
  SESSION_UPDATED: "SESSION_UPDATED",
  SESSION_DESTROYED: "SESSION_DESTROYED",
  PIPELINE_PHASE: "PIPELINE_PHASE",
  ENTITLEMENT_CHANGED: "ENTITLEMENT_CHANGED",
  ENTITLEMENT_BLOCKED: "ENTITLEMENT_BLOCKED",
  INTRO_STARTED: "INTRO_STARTED",
  INTRO_SKIPPED: "INTRO_SKIPPED",
  INTRO_ENDED: "INTRO_ENDED",
  AD_BREAK_START: "AD_BREAK_START",
  AD_BREAK_END: "AD_BREAK_END",
  LIVE_STATUS: "LIVE_STATUS",
  RECOMMENDATION_REQUEST: "RECOMMENDATION_REQUEST",
});

/** @type {Record<string, string>} */
var PLAYER_TO_PLATFORM = Object.freeze({
  PlayerReady: PlaybackPlatformEvent.READY,
  PlaybackStarted: PlaybackPlatformEvent.PLAY,
  PlaybackPaused: PlaybackPlatformEvent.PAUSE,
  PlaybackStopped: PlaybackPlatformEvent.STOP,
  PlaybackEnded: PlaybackPlatformEvent.ENDED,
  BufferStarted: PlaybackPlatformEvent.BUFFERING,
  BufferEnded: PlaybackPlatformEvent.BUFFERED,
  SeekStarted: PlaybackPlatformEvent.SEEK_START,
  SeekCompleted: PlaybackPlatformEvent.SEEK_COMPLETE,
  QualityChanged: PlaybackPlatformEvent.QUALITY_CHANGED,
  PlaybackError: PlaybackPlatformEvent.ERROR,
  TimeUpdate: PlaybackPlatformEvent.PROGRESS,
  VolumeChanged: PlaybackPlatformEvent.VOLUME_CHANGED,
  MuteChanged: PlaybackPlatformEvent.MUTE_CHANGED,
  RateChanged: PlaybackPlatformEvent.PLAYBACK_RATE_CHANGED,
  FullscreenEnter: PlaybackPlatformEvent.FULLSCREEN_ENTER,
  FullscreenExit: PlaybackPlatformEvent.FULLSCREEN_EXIT,
  TextTrackSelected: PlaybackPlatformEvent.SUBTITLE_CHANGED,
  AudioTrackSelected: PlaybackPlatformEvent.AUDIO_TRACK_CHANGED,
  StateChanged: PlaybackPlatformEvent.STATE_CHANGED,
  SourceChanged: PlaybackPlatformEvent.SOURCE_CHANGED,
  ProviderChanged: PlaybackPlatformEvent.PROVIDER_CHANGED,
});

/** @type {Record<string, string>} */
var PLATFORM_TO_PLAYER = Object.freeze({
  READY: PlayerEvent.PLAYER_READY,
  PLAY: PlayerEvent.PLAYBACK_STARTED,
  PAUSE: PlayerEvent.PLAYBACK_PAUSED,
  STOP: PlayerEvent.PLAYBACK_STOPPED,
  ENDED: PlayerEvent.PLAYBACK_ENDED,
  BUFFERING: PlayerEvent.BUFFER_STARTED,
  BUFFERED: PlayerEvent.BUFFER_ENDED,
  SEEK_START: PlayerEvent.SEEK_STARTED,
  SEEK_COMPLETE: PlayerEvent.SEEK_COMPLETED,
  QUALITY_CHANGED: PlayerEvent.QUALITY_CHANGED,
  ERROR: PlayerEvent.PLAYBACK_ERROR,
  PROGRESS: PlayerEvent.TIME_UPDATE,
  VOLUME_CHANGED: PlayerEvent.VOLUME_CHANGED,
  MUTE_CHANGED: PlayerEvent.MUTE_CHANGED,
  PLAYBACK_RATE_CHANGED: PlayerEvent.RATE_CHANGED,
  FULLSCREEN_ENTER: PlayerEvent.FULLSCREEN_ENTER,
  FULLSCREEN_EXIT: PlayerEvent.FULLSCREEN_EXIT,
  SUBTITLE_CHANGED: PlayerEvent.TEXT_TRACK_SELECTED,
  AUDIO_TRACK_CHANGED: PlayerEvent.AUDIO_TRACK_SELECTED,
  STATE_CHANGED: PlayerEvent.STATE_CHANGED,
  SOURCE_CHANGED: PlayerEvent.SOURCE_CHANGED,
  PROVIDER_CHANGED: PlayerEvent.PROVIDER_CHANGED,
});

/**
 * @param {string} playerEventName
 * @returns {string|null}
 */
export function mapPlayerEventToPlatform(playerEventName) {
  return PLAYER_TO_PLATFORM[playerEventName] || null;
}

/**
 * @param {string} platformEventName
 * @returns {string|null}
 */
export function mapPlatformEventToPlayer(platformEventName) {
  return PLATFORM_TO_PLAYER[platformEventName] || null;
}

/**
 * Expand PlayerEvent with Sprint 6A aliases (same string values as platform where mapped).
 * Keeps existing PlayerEvent exports intact.
 */
export function extendPlayerEventAliases(PlayerEventObj) {
  return Object.assign({}, PlayerEventObj, {
    READY: PlayerEventObj.PLAYER_READY,
    PLAY: PlayerEventObj.PLAYBACK_STARTED,
    PAUSE: PlayerEventObj.PLAYBACK_PAUSED,
    STOP: "PlaybackStopped",
    SEEK_START: PlayerEventObj.SEEK_STARTED,
    SEEK_COMPLETE: PlayerEventObj.SEEK_COMPLETED,
    PROGRESS: PlayerEventObj.TIME_UPDATE,
    BUFFERING: PlayerEventObj.BUFFER_STARTED,
    BUFFERED: PlayerEventObj.BUFFER_ENDED,
    ENDED: PlayerEventObj.PLAYBACK_ENDED,
    ERROR: PlayerEventObj.PLAYBACK_ERROR,
    MUTE_CHANGED: "MuteChanged",
    PLAYBACK_RATE_CHANGED: PlayerEventObj.RATE_CHANGED,
    FULLSCREEN_ENTER: "FullscreenEnter",
    FULLSCREEN_EXIT: "FullscreenExit",
    SUBTITLE_CHANGED: PlayerEventObj.TEXT_TRACK_SELECTED,
    AUDIO_TRACK_CHANGED: PlayerEventObj.AUDIO_TRACK_SELECTED,
  });
}

export default PlaybackPlatformEvent;
